import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { Button, Icons } from 'sarsaparilla';
import { get, isEmpty, isEqual, isNil } from 'lodash';
import validator from 'validator';

import SelectCustomerModal from 'ui-field-sales/src/components/cart/SelectCustomerModal';
import {
  PERMIT_ISSUED,
  PERMIT_COMPLETE,
  LOTTERY_AWARDED,
  LOTTERY_APPLIED,
  PERMIT_IN_REVIEW,
  PERMIT_REVIEWED,
  LOTTERY_AWARDED_IN_PROGRESS,
  PERMIT_IN_PROGRESS,
} from 'ui-permit/src/constants';
import { readRuleValue, issuanceStatuses, getClassName } from 'ui-permit/src/utils/utils';

import {
  GROUP_DETAIL_ALT_PROP_PATH,
  GROUP_DETAIL_PROP_PATH,
  GROUP_DETAIL_ALT_CONFIRM_PROP_PATH,
} from '../constants/propPaths';
import { BEAUTIFY_ENTRY_TYPES } from '../utils/constants';
import AlternatePermitHolderNameEmailEntry from "./AlternatePermitHolderNameEmailEntry";
import AlternatePermitHolderNamePhoneEntry from "./AlternatePermitHolderNamePhoneEntry";

const defaultDataBindingMap = {
  [GROUP_DETAIL_ALT_PROP_PATH]: 'alt_group_leaders',
  [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
  [GROUP_DETAIL_ALT_CONFIRM_PROP_PATH]: 'is_confirmed'
};

const defaultAttributes = {};

export default (AlternatePermitHolderEntry, issuanceShape, dataBindingsMap = defaultDataBindingMap, attributes = defaultAttributes, ) => {
  class AlternatePermitHolder extends React.Component {
    state = {
      modalIsOpen: false,
      customerSelectionModalOpen: false
    }

    onFormValidate = (isInValid, error, path) => this.props
        .pActions.formValidationChange(`${this.props.reservation.id}_${path}`, isInValid ? error : null)

    closeModal = () => this.setState({ modalIsOpen: false })

    openModal = () => this.setState({ modalIsOpen: true })

    addLeader = (leader = {}) => {
      if (!leader.walkUp) {
        let newGroupDetailAlt = get(this.props.reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]);
        // to avoid errors when it's null
        if (isNil(newGroupDetailAlt)) {
          newGroupDetailAlt = [];
        }
        const newLeader = {
          account_id: leader.account_id,
          first_name: leader.first_name,
          email: leader.email,
          cell_phone: leader.cell_phone,
          last_name: leader.last_name
        };

        const maxAlternateLeaders = this.props.maxAlternates ? this.props.maxAlternates: readRuleValue('MaxAlternateLeaders', this.props.permit.rules);
        const currentCount = newGroupDetailAlt.length;
        const condition = currentCount + 1 <= maxAlternateLeaders;
        if (condition) {
          const addLeader = {
            key: `${this.props.reservation.id}.${currentCount}`,
            ...newLeader
          };
          newGroupDetailAlt.push(addLeader);
        }
        this.props.pActions.updatePendingReservation(this.props.reservation.id, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], newGroupDetailAlt);
      }
    }

    removeLeader(index) {
      const newGroupDetailAlt = get(this.props.reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], []);
      if (newGroupDetailAlt.length > 0) {
        newGroupDetailAlt.splice(index, 1);
        this.props.pActions.updatePendingReservation(this.props.reservation.id, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], newGroupDetailAlt);
      }
    }

    handleModifyLeader(index, leader) {
      const { pActions, reservation } = this.props;
      const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}]`;
      pActions.updatePendingReservation(reservation.id, path, leader);
    }

    handleAlternateLeaderValueChange = (index, key, value) => {
      const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${this.props.entryType}`;
      const { pActions, reservation } = this.props;

      pActions.updatePendingReservation(reservation.id, path, value);
    }

    handleAlternateLeaderValidation(index, isInValid, error) {
      const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${this.props.entryType}`;
      if (isInValid) {
        this.props.pActions.formValidationChange(`${this.props.reservation.id}_${path}`, error);
      }
      else {
        this.props.pActions.formValidationChange(`${this.props.reservation.id}_${path}`, null);
      }
    }

    handleAlternateLeaderConfirm(index) {
      const { reservation, pActions } = this.props;
      pActions.updatePendingReservation(
        reservation.id,
        `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}][${dataBindingsMap[GROUP_DETAIL_ALT_CONFIRM_PROP_PATH]}]`,
        true
      );
    }

    alternateLeaderValidatorMethod(index, value) {
      const {entryType} = this.props
      if (isEmpty(value)) {
        return false;
      }

      if (entryType === "email" && !validator.isEmail(value)) {
        return true;
      }

      if (entryType === "cell_phone" && !validator.isMobilePhone(value)) {
        return true;
      }

      return isEqual(get(this.props.reservation, `${dataBindingsMap[GROUP_DETAIL_PROP_PATH]}.${entryType}`, ''), value) ||
        get(this.props.reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], []).filter((alt) => (entryType === "email" ? isEqual(alt.email, value) : isEqual(alt.cell_phone, value))).length > 1;
    }

    addNewAlternateLeader = () => {
      if (AlternatePermitHolderEntry?.name === 'AlternatePermitHolderInternalEntry') {
        this.setState({ customerSelectionModalOpen: true });
      } else {
        this.addLeader();
      }
    };

    isDisabled = (status) => {
      const { hasPermission, isInternal, isReadOnly, reservation } = this.props;
      const { lottery_awarded: isLotteryAwarded } = reservation ?? {};
      if (hasPermission && isInternal && !isReadOnly) {
        return false;
      }

      return (
        (status === issuanceStatuses.get(PERMIT_ISSUED)) ||
        (status === issuanceStatuses.get(PERMIT_COMPLETE)) ||
        (status === issuanceStatuses.get(PERMIT_IN_REVIEW)) ||
        (status === issuanceStatuses.get(PERMIT_REVIEWED)) ||
        (status === issuanceStatuses.get(LOTTERY_APPLIED)) ||
        (status === issuanceStatuses.get(LOTTERY_AWARDED)) ||
        (status === issuanceStatuses.get(LOTTERY_AWARDED_IN_PROGRESS)) ||
        (isLotteryAwarded && status === issuanceStatuses.get(PERMIT_IN_PROGRESS)) ||
        isReadOnly
      );
    }

    renderAltLeaders() {
      const { reservation, displayValidationError, validation, entryType, pActions, paths } = this.props;
      const { id, status } = reservation;

      const groupDetailAlt = get(reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], []);
      if (!groupDetailAlt || groupDetailAlt.length === 0) {
        return null;
      }

      return groupDetailAlt.map((leader, index) => {
        const path = `${dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH]}[${index}].${entryType}`;
        const className = getClassName(get(validation, `${id}_${path}`), displayValidationError);
        const altConfirm = dataBindingsMap[GROUP_DETAIL_ALT_CONFIRM_PROP_PATH];
        if (AlternatePermitHolderEntry) {
          return (<AlternatePermitHolderEntry
              key={leader.key}
              leaderId={leader.key}
              leader={leader}
              index={index}
              isDisabled={this.isDisabled(status)}
              handleAlternateLeaderChange={this.handleAlternateLeaderValueChange}
              handleAlternateLeaderValidation={this.handleAlternateLeaderValidation.bind(this, index)}
              alternateLeaderValidatorMethod={this.alternateLeaderValidatorMethod.bind(this, index)}
              handleAlternateLeaderConfirm={this.handleAlternateLeaderConfirm.bind(this, index)}
              handleModifyLeader={this.handleModifyLeader.bind(this)}
              className={className}
              removeLeader={(ind) => this.removeLeader(ind)}
              pActions={pActions}
              reservationId={reservation.id}
              paths={paths}
              altConfirm={altConfirm} />)
        }

        return entryType === "email" ? (
          <AlternatePermitHolderNameEmailEntry
            key={leader.key}
            leaderId={leader.key}
            leader={leader}
            index={index}
            isDisabled={this.isDisabled(status)}
            handleAlternateLeaderChange={this.handleAlternateLeaderValueChange}
            handleAlternateLeaderValidation={this.handleAlternateLeaderValidation.bind(this, index)}
            alternateLeaderValidatorMethod={this.alternateLeaderValidatorMethod.bind(this, index)}
            handleAlternateLeaderConfirm={this.handleAlternateLeaderConfirm.bind(this, index)}
            className={className}
            removeLeader={(ind) => this.removeLeader(ind)}
            pActions={this.props.pActions}
            reservationId={this.props.reservation.id} />
        ) : <AlternatePermitHolderNamePhoneEntry
        key={leader.key}
        leaderId={leader.key}
        leader={leader}
        index={index}
        isDisabled={this.isDisabled(status)}
        handleAlternateLeaderChange={this.handleAlternateLeaderValueChange}
        handleAlternateLeaderValidation={this.handleAlternateLeaderValidation.bind(this, index)}
        alternateLeaderValidatorMethod={this.alternateLeaderValidatorMethod.bind(this, index)}
        handleAlternateLeaderConfirm={this.handleAlternateLeaderConfirm.bind(this, index)}
        className={className}
        removeLeader={(ind) => this.removeLeader(ind)}
        pActions={this.props.pActions}
        reservationId={this.props.reservation.id} /> ;
      });
    }

    renderModal() {
      const sections = this.props.alternateModalSections.length
        ? this.props.alternateModalSections
        : [
          {
            title: 'Why An Alternate Permit Holder?',
            paragraph: 'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.'
          },
          {
            title: 'How to Add an Alternate Permit Holder',
            paragraph: `Enter first name, last name and ${BEAUTIFY_ENTRY_TYPES[this.props.entryType]} for an alternate permit holder to be added to permit. Once the permit is purchased, the designated alternate(s) cannot be modified.`
          }
        ];

      return (
        <Modal
          isOpen={this.state.modalIsOpen}
          role="complementary"
          onRequestClose={this.closeModal}
          className={{
            base: 'rec-modal-dialog-base',
            afterOpen: 'rec-modal-dialog-after-open-md',
            beforeClose: 'rec-modal-dialog-before-close'
          }}
          overlayClassName={{
            base: 'rec-overlay-base',
            afterOpen: 'rec-overlay-after-open',
            beforeClose: 'rec-overlay-before-close'
          }}
          contentLabel="Alternate Permit Holders">
          <div className="rec-modal-content-wrap permit-alternate-permit-modal">
            <Button
              className="rec-close-modal"
              onClick={this.closeModal}
              screenReaderTextBefore="Close" >
              <Icons.IconClose />
            </Button>
            <div className="rec-modal-title"><Icons.IconInfo /> Alternate Permit Holders</div>
            <div className="rec-modal-availability-wrap">
              {sections.map((section) => (
                <div key={section.title}>
                  <h2 className="h6 rec-modal-section-title" role="dialog" aria-label={section.title}>{section.title}</h2>
                  <p role="dialog" aria-label={section.paragraph}>
                    {section.paragraph}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )
    }

    render() {
      const { alternateMessageDetails, reservation, maxAlternates, entryType} = this.props;
      const maxAlternateLeaders = maxAlternates || readRuleValue('MaxAlternateLeaders', this.props.permit.rules);
      const { status } = reservation;
      const newGroupDetailAlt = get(reservation, dataBindingsMap[GROUP_DETAIL_ALT_PROP_PATH], []) ?? [];

      return (
        <div className="per-section-group">
          <div className="leader-container">
            {this.renderModal()}
          </div>
          <div className="permit-alt-leader">
            <div className="permit-alt-leader-form">
              <div className="permit-alt-leader-msg">
                <Icons.IconInfo />
                <p>
                  You may select a max of {maxAlternateLeaders} other people to act as alternate permit holders on your trip.&nbsp;
                  {alternateMessageDetails !== null && alternateMessageDetails !== undefined
                      ? alternateMessageDetails
                      : `Please enter a valid ${BEAUTIFY_ENTRY_TYPES[entryType]} for each of the intended holders.`
                  }
                  &nbsp;See <button className="rec-button-link" onClick={this.openModal} type="button">Alternate Permit Holders</button> for more information.
                </p>
              </div>
              {this.renderAltLeaders()}
            </div>
            <div className="permit-add-icon">
              <Button
                isDisabled={this.isDisabled(status) || newGroupDetailAlt.length >= maxAlternateLeaders}
                appearance="link"
                onClick={this.addNewAlternateLeader}
                iconBeforeElement={<Icons.IconAddCircle />}>
                Add alternate permit holder ({maxAlternateLeaders} max)
              </Button>
            </div>
          </div>
          <SelectCustomerModal
            title="Select Alternate Permit Holder"
            isOpen={this.state.customerSelectionModalOpen}
            closeModal={() => this.setState({ customerSelectionModalOpen: false })}
            selectCustomer={this.addLeader} />
        </div>
      );
    }
  }

  AlternatePermitHolder.propTypes = {
    pActions: PropTypes.shape({
      formValidationChange: PropTypes.func.isRequired,
      updatePendingReservation: PropTypes.func.isRequired
    }).isRequired,
    permit: PropTypes.shape({
      rules: PropTypes.arrayOf(PropTypes.shape({}))
    }).isRequired,
    validation: PropTypes.shape({}).isRequired,
    alternateMessageDetails: PropTypes.node,
    alternateModalSections: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      paragraph: PropTypes.node
    })),
    displayValidationError: PropTypes.bool,
    hasPermission: PropTypes.bool,
    isInternal: PropTypes.bool,
    maxAlternates: PropTypes.number,
    reservation: issuanceShape,
    paths: PropTypes.shape({
      email: PropTypes.string,
      last_name: PropTypes.string,
      first_name: PropTypes.string,
      alt_group_detail: PropTypes.string
    }),
    entryType: PropTypes.string,
    isReadOnly: PropTypes.bool
  }

  AlternatePermitHolder.defaultProps = {
    alternateMessageDetails: null,
    alternateModalSections: [],
    hasPermission: true,
    isInternal: false,
    entryType: "email"
  }

  AlternatePermitHolder.attributes = attributes;

  AlternatePermitHolder.componentName = "AlternatePermitHolder";

  return AlternatePermitHolder;
}
